import { ScrollText } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SegmentedControl } from '@/shared/components';
import { GuestPhoneFrame } from '@/features/guest/preview/GuestPhoneFrame';
import { guestRules, type RulesDraft, type RulesLang } from './houseRulesModel';

/**
 * Die Hausordnung, wie das Portal sie zeigt — mit derselben Sprachwahl: die des Gasts, sonst die
 * andere Fassung. Ein Entwurf ist als solcher gekennzeichnet, bis er gespeichert ist.
 */
export function HouseRulesPreview({
  draft,
  dirty,
  lang,
  onLangChange,
}: {
  draft: RulesDraft;
  dirty: boolean;
  lang: RulesLang;
  onLangChange: (lang: RulesLang) => void;
}) {
  const { i18n } = useTranslation();
  const locale = lang === 'De' ? 'de' : 'en';
  const t = i18n.getFixedT(locale);
  const shown = guestRules(draft, lang);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-ink-soft text-[11px] font-semibold uppercase tracking-[0.08em]">
          So sieht es der Gast
        </span>
        <SegmentedControl
          size="touch"
          aria-label="Sprache der Vorschau"
          value={lang}
          onChange={onLangChange}
          options={[
            { value: 'De', label: 'DE' },
            { value: 'En', label: 'EN' },
          ]}
        />
      </div>
      <GuestPhoneFrame label="Handy-Vorschau Hausordnung" language={locale}>
        <div className="border-line bg-bg-elev rounded-xl border p-3">
          <p className="text-fg flex items-center gap-2 text-sm font-semibold">
            <ScrollText className="size-4" aria-hidden />
            {t('stations.stay.houseRules.title')}
          </p>
          {shown.text.trim() ? (
            <p className="text-fg mt-2 whitespace-pre-wrap text-[13px] leading-relaxed">
              {shown.text}
            </p>
          ) : (
            <p className="text-fg-mute mt-2 text-xs">
              Noch nicht hinterlegt – der Gast sieht diesen Teil nicht.
            </p>
          )}
        </div>
        {shown.fallback && (
          <p className="text-fg-dim text-xs">
            {lang === 'En' ? 'Englisch' : 'Deutsch'} fehlt – der Gast sieht die andere Fassung.
          </p>
        )}
        {dirty && (
          <p className="text-fg-dim text-xs">Entwurf – sichtbar erst nach dem Speichern.</p>
        )}
      </GuestPhoneFrame>
    </div>
  );
}
